// The session store: a Solid store is the truth. The socket patches it
// through applyDown — a Snapshot replaces the whole state, a Delta with a
// newer sequence patches one part, anything at or before the applied
// sequence is a replay and ignored. Commands mint optimistic history
// entries locally (local: true); the server's own event for the same
// turn replaces its twin. Turn traffic (transcript, reply, audio, …) is
// not state: the client hands it to frame listeners instead.
import { createStore, produce, reconcile } from "solid-js/store";
import {
  HistoryKind,
  type Down,
} from "@/gen/psypen/v1/session_pb";
import {
  emptyState,
  historyFromProto,
  linesFromProto,
  pendingFromProto,
  type SessionState,
  type StoredHistoryEvent,
  type StoredPendingAction,
  stateEventFromDown,
} from "@/session/types";

// A local twin mints the turn's id client-side (sendTyped today, mints a
// SAID); the server's first stored event for the same turn — whatever
// kind it stored, NOTED and INSERTED included — takes its place.
function replacesLocal(incoming: StoredHistoryEvent, existing: StoredHistoryEvent): boolean {
  return Boolean(existing.local) && existing.turn === incoming.turn;
}

export interface SessionStore {
  state: SessionState;
  applyDown(d: Down): void;
  applyLocal(ev: StoredHistoryEvent): void;
  restore(state: SessionState): void;
  // setPending is the optimistic hand the gate answers with: the box is
  // cleared (or replaced by what waits after it) before the stream's
  // own delta lands. Anything the socket says after still wins.
  setPending(p: StoredPendingAction | null): void;
  // pendingEpoch counts the stream's own words about the gate — every
  // snapshot and pending delta that wrote `pending`. The gate's
  // optimistic hand does not move it, so an answer in flight can tell
  // its own writes from the socket's (gate.ts): a changed epoch means
  // the stream spoke meanwhile, and the stream's word is the newer one.
  pendingEpoch(): number;
}

export function createSessionStore(): SessionStore {
  const [state, setState] = createStore<SessionState>(emptyState());
  let epoch = 0;

  function applyHistory(ev: StoredHistoryEvent): void {
    setState(
      "history",
      produce((list) => {
        // Same stored row twice (a replay after a snapshot is harmless
        // there) updates in place, keeping its position.
        const byId = ev.id > 0 ? list.findIndex((h) => h.id === ev.id && h.id > 0) : -1;
        if (byId >= 0) {
          list[byId] = ev;
          return;
        }
        const twin = list.findIndex((h) => replacesLocal(ev, h));
        if (twin >= 0) {
          list[twin] = ev;
          return;
        }
        list.push(ev);
      }),
    );
  }

  return {
    state,
    restore(saved) {
      setState(reconcile(saved, { merge: true }));
    },
    setPending(p) {
      setState("pending", p);
    },
    pendingEpoch: () => epoch,
    applyDown(d) {
      const event = stateEventFromDown(d);
      if (!event) return;
      switch (event.kind) {
        case "snapshot":
          // A snapshot, first or mid-stream, is the whole truth; a turn's
          // optimistic entry still in flight comes again as its stored
          // event, so dropping it here loses nothing.
          setState(reconcile(event.state, { merge: true }));
          epoch += 1;
          return;
        case "task":
          setState("task", event.task);
          return;
        case "delta": {
          const delta = event.delta;
          if (state.seq !== "" && delta.seq <= BigInt(state.seq)) return;
          switch (delta.change.case) {
            case "history":
              applyHistory(historyFromProto(delta.change.value));
              break;
            case "lines":
              setState("lines", reconcile(linesFromProto(delta.change.value)));
              break;
            case "pending":
              setState("pending", pendingFromProto(delta.change.value.action));
              epoch += 1;
              break;
            case undefined:
              break;
          }
          setState("seq", delta.seq.toString());
          return;
        }
      }
    },
    applyLocal(ev) {
      applyHistory({ ...ev, local: true });
    },
  };
}

// The store's own view of what a said utterance is, for optimistic adds.
export function localSaid(turn: string, text: string): StoredHistoryEvent {
  return {
    id: 0,
    turn,
    at: new Date().toISOString(),
    kind: HistoryKind.SAID,
    text,
    tool: "",
    input: "",
    error: false,
  };
}
